import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UtilityService } from '@eforall/common';
import { DashboardDialogFramePart, DialogAction, DialogContext, DialogService } from '@eforall/dashboard';
import { DialogMode, EngagementExternalRoleId, StaffRoleId } from '@interfaces';
import { PersonDialog } from '../../person-dialog';
import { EngagementEditorDialog, EngagementEditorDialogData } from '../engagement-editor';
import { EngagementViewerDialog } from '../engagement-viewer';
import { ContactRequestService } from './contact-request.service';

export interface ContactRequestDialogData {
	contactRequestId: number,
	personId: number,
	staffRoleId: StaffRoleId,
	externalRoleId: EngagementExternalRoleId | undefined
}

@Component({
	selector: 'contact-request-dialog',
	standalone: true,
	imports: [
		DashboardDialogFramePart
	],
	templateUrl: './contact-request.dialog.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './contact-request.dialog.scss'
})
export class ContactRequestDialog implements OnInit {

	public dialogRef = inject(MatDialogRef<ContactRequestDialog>);
	public dialogContext: DialogContext<ContactRequestDialogData> = inject(MAT_DIALOG_DATA);

	private readonly contactRequestService = inject(ContactRequestService);
	private readonly util = inject(UtilityService);
	private readonly dialogService = inject(DialogService);

	public readonly contactRequestDetails = computed(() => {
		const details = this.contactRequestService.contactRequestDetails();
		if (!details) return undefined;
		return {
			...details,
			requestedDateUTC: this.util.date.formatUTC(details.requestedDateUTC!, 'MM/DD/YYYY', 'No Time', 'en-US'),
		};
	});
	public readonly headerName = computed(() => {
		return this.contactRequestService.contactRequestDetails() ? 'Contact Request: ' + this.contactRequestService.contactRequestDetails()?.person.fullName : 'Prospect';
	});

	ngOnInit() {
		const data = this.dialogContext.data;
		if (data) {
			this.contactRequestService.getContactRequestById(data.contactRequestId!);
		}
	}

	public actions = computed<DialogAction[]>(() => {
		const details = this.contactRequestService.contactRequestDetails();
		const data = this.dialogContext.data;
		const actions: DialogAction[] = [
			{
				id: 'close',
				cssClass: signal(undefined),
				enabled: signal(true),
				visible: signal(true),
				text: signal('Close'),
				linkType: 'no-action',
				willCloseDialog: true,
			}
		];

		if (!details || !data) return [];

		const isClosed = details.closed;
		const engagementId = details.engagementId;


		// Engagement button logic
		if (engagementId !== undefined && engagementId !== null) {
			actions.unshift({
				id: 'openEngagement',
				cssClass: signal(undefined),
				enabled: signal(true),
				visible: signal(true),
				text: signal('Open Engagement'),
				linkType: 'callback',
				willCloseDialog: false,
				callback: async () => {
					if (engagementId !== undefined) {
						await this.dialogService.showCustom(EngagementViewerDialog, { data: { engagementId } }, 700, 700);
					}
				}
			});
		} else {
			actions.unshift({
				id: 'createEngagement',
				cssClass: signal(undefined),
				enabled: signal(true),
				visible: signal(true),
				text: signal('Create Engagement'),
				linkType: 'callback',
				willCloseDialog: false,
				callback: async () => {
					const dialogData: EngagementEditorDialogData = {
						mode: DialogMode.ADD,
						personId: data.personId,
						staffRoleId: data.staffRoleId,
						externalRoleId: data.externalRoleId,
					};
					const dialogAction = await this.dialogService.showCustom(EngagementEditorDialog, { data: dialogData }, 650, 800);

					// If save succeeds, callbackResult contains the created engagementId; otherwise, it is undefined.
					if (dialogAction?.callbackResult) {
						await this.contactRequestService.addEngagementIdToContactRequest(details.contactRequestId, dialogAction.callbackResult);
					}
				}
			});
		}

		if (isClosed) {
			actions.unshift({
				id: 'reopen',
				cssClass: signal(undefined),
				enabled: signal(true),
				visible: signal(true),
				text: signal('Reopen'),
				linkType: 'callback',
				willCloseDialog: false,
				callback: async () => {
					const yes = await this.dialogService.confirm('Are you sure you want to reopen this contact request? This action cannot be undone.', 'Yes', 'No', [], 400, 200);
					if (yes) {
						await this.contactRequestService.updateClosedContactRequest(false, details.contactRequestId);
					}
				}
			});
		} else {
			actions.unshift({
				id: 'markAsClosed',
				cssClass: signal(undefined),
				enabled: signal(true),
				visible: signal(true),
				text: signal('Mark as Closed'),
				linkType: 'callback',
				willCloseDialog: false,
				callback: async () => {
					const yes = await this.dialogService.confirm('Are you sure you want to mark this contact request as closed? This action cannot be undone.', 'Yes', 'No', [], 400, 200);
					if (yes) {
						await this.contactRequestService.updateClosedContactRequest(true, details.contactRequestId);
					}
				}
			});
		}


		return actions;
	});

	public openPersonDialog(personId: number) {
		this.dialogService.showCustom(PersonDialog, { data: { personId, toEdit: false } }, 800, 800);
	}

}